/* Tasarım belgesi — panelin Görünüm editörünün yazdığı, üreticinin okuduğu tek şey.

   Dört parça:

     tokens   bütün sitenin teması: koyu/açık renkler, yazı tipleri, ölçek.
              Her alan isteğe bağlı; boş olan style.css'teki değerde kalır.
     home     ana sayfa, blok belgesi olarak (blocks.ts). Yoksa üretici
              bugünkü sayfayı (tools/src/blocks/home.ts) basıyor.
     shared   her sayfanın menüsü ve footer'ı (`nav`, `footer` blokları).
     pages    works, blog ve 404'ün başlık bölgeleri (`page` blokları).

   Eksik olan her parça bugünkü sitenin karşılığıyla dolduruluyor
   (tools/src/blocks/defaults.ts).

   Belge `site_design` tablosunda iki satır olarak duruyor: `draft` editörün
   kaydettiği, `live` üreticinin okuduğu (routes/adminDesign.ts).

   Renkler yalnızca `#rrggbb`, yazı tipleri yalnızca listeden: CSS'e serbest
   metin girmiyor. Blok metinlerinin güvenliği blocks.ts'te. */

import { z } from 'zod';
import { pageSchema } from './blocks.ts';
import { FONTS, type FontName } from './fonts.ts';

export { DEFAULT_FONTS, FONTS, type FontName } from './fonts.ts';

const FONT_NAMES = Object.keys(FONTS) as [FontName, ...FontName[]];

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Renk #rrggbb biçiminde olmalı.');

/** Tema renkleri. Hepsi isteğe bağlı: boş olan style.css'teki değerde kalır. */
export const paletteSchema = z
  .object({
    bg: hex.optional(),
    bgElev: hex.optional(),
    fg: hex.optional(),
    fgMuted: hex.optional(),
    accent: hex.optional()
  })
  .strict();

export type Palette = z.infer<typeof paletteSchema>;

export const tokensSchema = z
  .object({
    dark: paletteSchema.default({}),
    light: paletteSchema.default({}),
    fontDisplay: z.enum(FONT_NAMES).optional(),
    fontBody: z.enum(FONT_NAMES).optional(),
    /** Bütün yazı ölçeğinin çarpanı; 1 = bugünkü. */
    typeScale: z.number().min(0.8).max(1.3).optional()
  })
  .strict();

export type Tokens = z.infer<typeof tokensSchema>;

export const designSchema = z
  .object({
    version: z.literal(1),
    tokens: tokensSchema.default({ dark: {}, light: {} }),
    home: pageSchema.optional(),
    /** Ortak alanlar: `nav` ve `footer` blokları. Yoksa bugünkü menü ve footer. */
    shared: pageSchema.optional(),
    /** Diğer sayfaların bölgeleri — her biri tek bir `page` bloğu. */
    pages: z
      .object({ works: pageSchema.optional(), blog: pageSchema.optional(), notFound: pageSchema.optional() })
      .strict()
      .optional()
  })
  .strict();

export type Design = z.infer<typeof designSchema>;

/** Bugünkü site: tema ezmesi yok, ana sayfa varsayılan belge. */
export function defaultDesign(): Design {
  return { version: 1, tokens: { dark: {}, light: {} } };
}
